import { Entry } from "contentful";
import singletonRouter from "next/router";
import {
  Configure,
  InstantSearch,
  InstantSearchServerState,
  InstantSearchSSRProvider,
  SearchBox,
  SortBy,
} from "react-instantsearch";
import { createInstantSearchRouterNext } from "react-instantsearch-router-nextjs";

import IconSearch from "@/icons/icon-search";
import Icon from "~/components/common/icon";
import SearchProductGridAlgolia from "~/components/products/search-product-grid-algolia";
import { searchClient } from "~/components/search/search-client";
import Algolia from "~/types/algolia";
import CommonUtils from "~/utils/common-utils";

import Facets from "../products/facets";

type SearchSkeletonProps = {
  serverState?: InstantSearchServerState;
  serverUrl: string;
  algoliaIndexId: string;
  noResultsComponents?: Entry<unknown>[];
  algoliaUserToken: string;
};

export default function SearchSkeleton(props: SearchSkeletonProps) {
  const { serverState, serverUrl, algoliaUserToken, algoliaIndexId, noResultsComponents } = props;

  const algoliaSearchConfig = CommonUtils.parseJsonSafely(
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_CONFIG!
  ) as Algolia.Configuration;

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaIndexId}
        routing={{
          stateMapping: {
            stateToRoute: (uiState: any) => {
              const indexUiState = uiState[algoliaIndexId];

              const { refinementList, range, ratingMenu, sortBy, query, ...rest } = indexUiState;

              return {
                [algoliaIndexId]: {
                  query,
                  refinementList,
                  range,
                  ratingMenu,
                  sortBy,
                },
              };
            },
            routeToState(routeState) {
              if (!routeState?.[algoliaIndexId]) {
                return routeState;
              }

              const { refinementList, range, ratingMenu, sortBy, query, ...rest } = routeState[algoliaIndexId];

              return {
                [algoliaIndexId]: {
                  query,
                  refinementList,
                  range,
                  ratingMenu,
                  sortBy,
                },
              };
            },
          },
          router: createInstantSearchRouterNext({
            singletonRouter,
            serverUrl,
            routerOptions: { cleanUrlOnDispose: false },
          }),
        }}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        insights
      >
        <Configure {...algoliaSearchConfig} userToken={algoliaUserToken} />
        <Facets />
        <SortBy items={[]} hidden />
        {serverState ? (
          <SearchProductGridAlgolia
            noResultsComponents={noResultsComponents}
            initialResultsHits={serverState.initialResults[algoliaIndexId]?.results?.[0].hits || []}
            algoliaUserToken={algoliaUserToken}
          />
        ) : (
          <SearchBox
            submitIconComponent={({ classNames }) => <Icon className={classNames.submitIcon} icon={IconSearch} />}
          />
        )}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}
